import { useQuery } from '@apollo/client';
import { type FunctionComponent } from 'react';
import { configureRoute } from '../../../utils/routes/configure-route';
import { titleCase } from '../../../utils/string/title-case.utils';
import {
	type ArticleSearchTypeEnum,
	type ArticleSearchQuery,
	type ArticleSearchQueryVariables
} from '../../__generated__/graphql-client-types';
import { type Breadcrumb } from '../../components/breadcrumbs/breadcrumbs.component';
import { PageLoading } from '../../components/common-components/loading/loading.component';
import { PageContainer } from '../../components/common-components/page/page-container/page-container.component';
import { FiveHundred } from '../../components/error-components/500/500.component';
import { LearningCenterArticles } from '../../components/learning-center/learning-center-articles/learning-center-articles.component';
import { LearningCenterNav } from '../../components/learning-center/learning-center-nav/learning-center-nav.component';
import { ResponsiveSidebar } from '../../components/responsive-sidebar/responsive-sidebar.component';
import { PAGE_CONTENT_ELEMENT } from '../../constants/general';
import { LEARNING_CENTER_LINK } from '../../constants/links';
import { useTrackPageView } from '../../hooks/analytics/analytics.hooks';
import { usePagedSearchParams } from '../../hooks/paged-search-params/paged-search-params.hooks';
import { useRouteParams } from '../../hooks/routing/params.hook';
import { LEARNING_CENTER_SEARCH } from '../../queries/learning-center/learning-center.queries';
import { type ArticleSearch as ArticleSearchType } from '../../types/article.types';

const DEFAULT_PAGE_TITLE = 'Learning Center';
const DEFAULT_META_DESCRIPTION =
	'From inspiration to installation, click here to learn more about learning center from our team of experts.';

function generateBreadcrumbs(currentPage: string): Breadcrumb[] {
	const breadcrumbs: Breadcrumb[] = [{ name: DEFAULT_PAGE_TITLE, url: LEARNING_CENTER_LINK }];
	// needed to support page and topic pages
	if (currentPage !== DEFAULT_PAGE_TITLE) {
		breadcrumbs.push({ name: currentPage });
	}
	return breadcrumbs;
}

export function calculateSearchType(isRoom: boolean, isTopic: boolean): ArticleSearchTypeEnum {
	let searchType: ArticleSearchTypeEnum = 'ALL';
	if (isRoom) {
		searchType = 'BY_ROOM';
	}
	if (isTopic) {
		searchType = 'BY_TOPIC';
	}
	return searchType;
}

export function calculateCanonicalURL(room?: string, topic?: string): string {
	if (room) {
		return `/learn/room/${room}`;
	} else if (topic) {
		return `/learn/topic/${topic}`;
	} else {
		return '/learn';
	}
}

export function calculatePageGroup(room?: string, topic?: string): string {
	let pageGroup = 'learning-center';

	if (room) {
		pageGroup += ':room';
	} else if (topic) {
		pageGroup += ':topic';
	}

	return pageGroup;
}

type LearningCenterProps = ArticleSearchType & { pageTitle?: string };

const LearningCenter: FunctionComponent<LearningCenterProps> = ({ rooms, topics, pageTitle = DEFAULT_PAGE_TITLE, ...articlesProps }) => {
	const summary = `${articlesProps.count} Articles`;
	return (
		<section id={PAGE_CONTENT_ELEMENT}>
			<h1 className="mv1">{pageTitle}</h1>
			<p className="f6 mt0">
				Ideas, DIY guides, installation tips, and more to help you on your next project. From inspiration to installation®
			</p>
			<ResponsiveSidebar
				summary={summary}
				trigger="Filter Articles"
				content={<LearningCenterNav rooms={rooms} topics={topics} />}
				direction="right"
				childrenTheme="theme-grey-lighter">
				<LearningCenterArticles {...articlesProps} />
			</ResponsiveSidebar>
		</section>
	);
};

export const LearningCenterPage: FunctionComponent = () => {
	const { offset, limit } = usePagedSearchParams();
	const { room, topic } = useRouteParams<{ room?: string; topic?: string }>();
	const keyword = room || topic;
	const pageTitle = keyword ? titleCase(keyword) : DEFAULT_PAGE_TITLE;

	const pageName = 'learning center';
	useTrackPageView({ pageName }, { page: calculatePageGroup(room, topic) });

	const {
		loading,
		data: { articleSearch } = {},
		error
	} = useQuery<ArticleSearchQuery, ArticleSearchQueryVariables>(LEARNING_CENTER_SEARCH, {
		variables: {
			input: {
				offset,
				limit: limit - 1, // fetch one less then page size to account for injected email card
				searchType: calculateSearchType(Boolean(room), Boolean(topic)),
				keyword
			}
		}
	});

	if (error) {
		return <FiveHundred />;
	} else if (loading || !articleSearch) {
		return <PageLoading />;
	}
	return (
		<PageContainer
			canonicalURL={calculateCanonicalURL(room, topic)}
			pageTitle={pageTitle}
			metaDescription={DEFAULT_META_DESCRIPTION}
			breadcrumbs={generateBreadcrumbs(pageTitle)}
			showUpdateLocation={true}>
			{articleSearch && <LearningCenter {...articleSearch} pageTitle={pageTitle} />}
		</PageContainer>
	);
};

export const RoutedLearningCenterPage = configureRoute(
	{ path: ['/learn', '/learn/room/:room', '/learn/topic/:topic'] },
	LearningCenterPage
);
